import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw !== confirmPw) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPw });
      toast.success("Password reset — sign in with your new password");
      navigate("/login");
    } catch (err) { setError(apiError(err)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F9F8] flex items-center justify-center px-4">
      <div className="w-full max-w-[450px] fade-up">
        <div className="mb-6 flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-[#087F5B] flex items-center justify-center font-heading font-bold text-white text-sm shadow-sm">SP</span>
          <div>
            <p className="font-heading text-lg font-bold text-[#132238] tracking-tight leading-none">SupportPro Hub</p>
            <p className="text-[11px] text-[#637083] mt-0.5">by The Allicock Group</p>
          </div>
        </div>
        <div className="bg-white rounded-[18px] p-7 sm:p-9 shadow-[0_20px_50px_-20px_rgba(19,34,56,0.18)] border border-[#E5E8EC]">
          {!token ? (
            <div className="text-center py-4">
              <p className="text-sm text-[#637083]">This reset link is missing its token. Request a new one from the sign-in page.</p>
              <Link to="/login" data-testid="back-to-login-link" className="inline-block mt-4 text-sm font-semibold text-[#087F5B] hover:text-[#076B4D]">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="flex items-center gap-2 text-[#087F5B]">
                <KeyRound size={20} />
                <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#132238]">Reset your password</h1>
              </div>
              <p className="text-sm text-[#637083]">Choose a new password for your SupportPro Hub account.</p>
              <div>
                <label htmlFor="reset-new-password" className="text-sm font-medium text-[#132238]">New password</label>
                <input id="reset-new-password" data-testid="reset-new-password-input" type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-[#E5E8EC] px-4 py-3 text-base text-[#132238] focus:outline-none focus:ring-2 focus:ring-[#087F5B]/25 focus:border-[#087F5B] transition-colors duration-200" placeholder="At least 8 characters, letters + numbers" />
              </div>
              <div>
                <label htmlFor="reset-confirm-password" className="text-sm font-medium text-[#132238]">Confirm password</label>
                <input id="reset-confirm-password" data-testid="reset-confirm-password-input" type="password" required minLength={8} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-[#E5E8EC] px-4 py-3 text-base text-[#132238] focus:outline-none focus:ring-2 focus:ring-[#087F5B]/25 focus:border-[#087F5B] transition-colors duration-200" placeholder="Re-enter your new password" />
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
              <button data-testid="reset-submit-btn" disabled={loading}
                className="w-full bg-[#087F5B] hover:bg-[#076B4D] text-white font-semibold rounded-xl py-3.5 text-base transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 shadow-sm">
                {loading ? "Resetting…" : "Reset password"}
              </button>
              <Link to="/login" data-testid="reset-back-link" className="block text-center text-xs text-[#637083] hover:text-[#132238] transition-colors duration-200">Back to sign in</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
